import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { API_URL, wholesalerAPI } from '../../services/api';
import {
  Download, Upload, CheckCircle, XCircle, AlertCircle,
  FileText, TrendingUp, Loader, ArrowLeft, ArrowRight,
  Image, Eye
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import toast from 'react-hot-toast';


const BulkProductUpload = () => {
  const { t } = useTranslation(['wholesaler', 'products']);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationResults, setValidationResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Step 1: Template Download
  const handleDownloadTemplate = (language, format = 'csv') => {
    const endpoint = language === 'greek'
      ? '/products/bulk-template/greek'
      : '/products/bulk-template';

    const token = localStorage.getItem('token');
    const url = `${API_URL}${endpoint}?format=${format}`;

    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      a.download = `product_template_${language === 'greek' ? 'greek' : 'english'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    })
    .catch(error => {
      console.error('Error downloading template:', error);
      toast.error(t('bulkUpload.alerts.downloadFailed'));
    });
  };

  // Step 2: File Upload & Parsing
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validExts = ['.csv', '.xlsx', '.xls'];
    const hasValidExt = validExts.some(ext => file.name.toLowerCase().endsWith(ext));
    if (!hasValidExt) {
      toast.error(t('bulkUpload.alerts.csvOrExcel', 'Please select a CSV or Excel (.xlsx/.xls) file'));
      return;
    }

    setCsvFile(file);
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          resolve(data);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleParseCSV = async () => {
    if (!csvFile) {
      toast.error(t('bulkUpload.alerts.csvOrExcel', 'Please select a CSV or Excel file'));
      return;
    }

    const isExcel = csvFile.name.toLowerCase().endsWith('.xlsx') || csvFile.name.toLowerCase().endsWith('.xls');

    if (isExcel) {
      try {
        const data = await parseExcelFile(csvFile);
        setParsedData(data);
        setCurrentStep(3);
        handleValidation(data);
      } catch (error) {
        console.error('Excel parsing error:', error);
        toast.error(t('bulkUpload.alerts.parseFailed'));
      }
    } else {
      Papa.parse(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setParsedData(results.data);
          setCurrentStep(3);
          handleValidation(results.data);
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          toast.error(t('bulkUpload.alerts.parseFailed'));
        }
      });
    }
  };

  // Step 3: Validation
  const handleValidation = async (products) => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/products/bulk-validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products })
      });

      const data = await response.json();
      setValidationResults(data);
    } catch (error) {
      console.error('Validation error:', error);
      toast.error(t('bulkUpload.alerts.fixValidation'));
    }
  };

  // Step 4: Import
  const handleImport = async () => {
    const limitCheck = validationResults?.productLimitInfo;
    const isLimitExceeded = limitCheck && limitCheck.wouldExceed;

    if (!validationResults || !validationResults.valid || isLimitExceeded) {
      toast.error(t('bulkUpload.alerts.fixValidation'));
      return;
    }

    setImporting(true);
    setCurrentStep(4);

    const token = localStorage.getItem('token');

    const validProducts = validationResults.products
      .filter(p => p.isValid)
      .map(p => p.product);

    try {
      const response = await fetch(`${API_URL}/products/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: validProducts })
      });

      const data = await response.json();
      setImportResults(data);
      setCurrentStep(5);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(t('bulkUpload.alerts.downloadFailed'));
    } finally {
      setImporting(false);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setCsvFile(null);
    setParsedData([]);
    setValidationResults(null);
    setImportResults(null);
    setImporting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-20 lg:pt-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/wholesaler/products')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('bulkUpload.backToProducts')}
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{t('bulkUpload.title')}</h1>
          <p className="text-gray-600 mt-2">{t('bulkUpload.subtitle')}</p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-bold
                  ${currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}
                  ${currentStep === step ? 'ring-4 ring-blue-200' : ''}
                `}>
                  {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                </div>
                {step < 5 && (
                  <div className={`w-16 md:w-32 h-1 mx-2 ${currentStep > step ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 text-sm">
            <span className={currentStep >= 1 ? 'text-blue-600 font-semibold' : 'text-gray-500'}>{t('bulkUpload.steps.download')}</span>
            <span className={currentStep >= 2 ? 'text-blue-600 font-semibold' : 'text-gray-500'}>{t('bulkUpload.steps.upload')}</span>
            <span className={currentStep >= 3 ? 'text-blue-600 font-semibold' : 'text-gray-500'}>{t('bulkUpload.steps.validate')}</span>
            <span className={currentStep >= 4 ? 'text-blue-600 font-semibold' : 'text-gray-500'}>{t('bulkUpload.steps.import')}</span>
            <span className={currentStep >= 5 ? 'text-blue-600 font-semibold' : 'text-gray-500'}>{t('bulkUpload.steps.results')}</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          {/* Step 1: Download Template */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('bulkUpload.step1.title')}</h2>
              <p className="text-gray-600 mb-6">{t('bulkUpload.step1.description')}</p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* English Template */}
                <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <FileText className="w-12 h-12 text-blue-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{t('bulkUpload.step1.englishTemplate')}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{t('bulkUpload.step1.englishDesc')}</p>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleDownloadTemplate('english', 'csv')}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {t('bulkUpload.step1.downloadEnglish')} (CSV)
                    </button>
                    <button
                      onClick={() => handleDownloadTemplate('english', 'xlsx')}
                      className="w-full bg-blue-50 text-blue-700 border border-blue-300 py-2 px-4 rounded-lg hover:bg-blue-100 flex items-center justify-center"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {t('bulkUpload.step1.downloadEnglish')} (XLSX)
                    </button>
                  </div>
                </div>

                {/* Greek Template */}
                <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <FileText className="w-12 h-12 text-green-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{t('bulkUpload.step1.greekTemplate')}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{t('bulkUpload.step1.greekDesc')}</p>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleDownloadTemplate('greek', 'csv')}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 flex items-center justify-center"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {t('bulkUpload.step1.downloadGreek')} (CSV)
                    </button>
                    <button
                      onClick={() => handleDownloadTemplate('greek', 'xlsx')}
                      className="w-full bg-green-50 text-green-700 border border-green-300 py-2 px-4 rounded-lg hover:bg-green-100 flex items-center justify-center"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {t('bulkUpload.step1.downloadGreek')} (XLSX)
                    </button>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-2">📋 {t('bulkUpload.step1.instructions')}:</h4>
                <ol className="list-decimal list-inside text-blue-800 space-y-1 text-sm">
                  <li>{t('bulkUpload.step1.inst1')}</li>
                  <li>{t('bulkUpload.step1.inst2')}</li>
                  <li>{t('bulkUpload.step1.inst3')}</li>
                  <li>{t('bulkUpload.step1.inst4')}</li>
                  <li>{t('bulkUpload.step1.inst5')}</li>
                </ol>
              </div>

              {/* Required Fields Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-amber-900 mb-2">⚠️ {t('bulkUpload.step1.requiredFields')}:</h4>
                <ul className="list-disc list-inside text-amber-800 space-y-1 text-sm">
                  <li>{t('bulkUpload.step1.fieldName')}</li>
                  <li>{t('bulkUpload.step1.fieldCategory')}</li>
                  <li>{t('bulkUpload.step1.fieldPrice')}</li>
                  <li>{t('bulkUpload.step1.fieldUnit')}</li>
                </ul>
              </div>

              {/* Optional: visible column */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">👁️ {t('bulkUpload.step1.optionalFields', 'Optional columns')}:</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                  <li>{t('bulkUpload.step1.fieldVisible', 'visible — TRUE/FALSE. Defaults to TRUE if blank. Set FALSE for under-test or paused products you don\'t want businesses to see.')}</li>
                </ul>
              </div>

              <button
                onClick={() => setCurrentStep(2)}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 flex items-center justify-center font-semibold"
              >
                {t('bulkUpload.step1.next')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          )}

          {/* Step 2: Upload CSV */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('bulkUpload.step2.title')}</h2>
              <p className="text-gray-600 mb-6">{t('bulkUpload.step2.description')}</p>

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 text-center hover:border-blue-500 transition-colors">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer inline-block bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700"
                >
                  {t('bulkUpload.step2.chooseFile')}
                </label>
                <p className="text-gray-500 text-sm mt-4">{t('bulkUpload.step2.dragDrop')}</p>
                {csvFile && (
                  <div className="mt-4 inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB)
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  {t('bulkUpload.step2.back')}
                </button>
                <button
                  onClick={handleParseCSV}
                  disabled={!csvFile}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center font-semibold"
                >
                  {t('bulkUpload.step2.parseValidate')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Validation Results */}
          {currentStep === 3 && validationResults && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('bulkUpload.steps.validate')}</h2>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{validationResults.total}</div>
                  <div className="text-sm text-gray-600">{t('bulkUpload.step3.row')}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{validationResults.valid}</div>
                  <div className="text-sm text-gray-600">{t('bulkUpload.step3.validProducts')}</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{validationResults.total - validationResults.valid}</div>
                  <div className="text-sm text-gray-600">{t('bulkUpload.step3.validationErrors')}</div>
                </div>
                
                {validationResults.productLimitInfo && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {validationResults.productLimitInfo.currentCount}/
                      {validationResults.productLimitInfo.limit === -1 ? '∞' : validationResults.productLimitInfo.limit}
                    </div>
                    <div className="text-sm text-gray-600">{t('bulkUpload.step3.productLimit')}</div>
                  </div>
                )}
              </div>

              {/* Product Limit Warning */}
              {validationResults.productLimitInfo && validationResults.productLimitInfo.wouldExceed && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">{t('bulkUpload.step3.limitExceeded')}</h4>
                      <p className="text-red-800 text-sm">
                        {t('bulkUpload.step3.limitExceededDesc', {
                          total: validationResults.total,
                          limit: validationResults.productLimitInfo.limit === -1 ? 'Unlimited' : validationResults.productLimitInfo.limit,
                          current: validationResults.productLimitInfo.currentCount
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Errors */}
              {validationResults.errors && validationResults.errors.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-red-600 mb-3">
                    ❌ {t('bulkUpload.step3.validationErrors')} ({validationResults.errors.length})
                  </h3>
                  <div className="max-h-96 overflow-y-auto border border-red-200 rounded-lg">
                    {validationResults.errors.map((error, idx) => (
                      <div key={idx} className="border-b border-red-100 last:border-b-0 p-4 bg-red-50">
                        <div className="font-semibold text-red-900 mb-2">{t('bulkUpload.step3.row')} {error.row}</div>
                        <ul className="list-disc list-inside text-red-800 text-sm space-y-1">
                          {error.errors.map((err, errIdx) => (
                            <li key={errIdx}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Valid Products Preview */}
              {validationResults.valid > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-green-600 mb-3">
                    ✅ {t('bulkUpload.step3.validProducts')} ({validationResults.valid})
                  </h3>
                  <div className="max-h-96 overflow-y-auto border border-green-200 rounded-lg">
                    <table className="min-w-full divide-y divide-green-200">
                      <thead className="bg-green-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-green-900">{t('bulkUpload.step3.name')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-green-900">{t('bulkUpload.step3.category')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-green-900">{t('bulkUpload.step3.price')}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-green-900">{t('bulkUpload.step3.unit')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {validationResults.products.filter(p => p.isValid).slice(0, 20).map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-3 text-sm">{item.product.name}</td>
                            <td className="px-4 py-3 text-sm">{t(`products:categories.${item.product.category}`, item.product.category)}</td>
                            <td className="px-4 py-3 text-sm">€{item.product.price}</td>
                            <td className="px-4 py-3 text-sm">{item.product.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {validationResults.valid > 20 && (
                      <div className="bg-gray-50 p-3 text-center text-sm text-gray-600">
                        {t('bulkUpload.step3.andMore', { count: validationResults.valid - 20 })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setCurrentStep(2);
                    setValidationResults(null);
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  {t('bulkUpload.step3.fixReupload')}
                </button>
                <button
                  onClick={handleImport}
                  disabled={
                    !validationResults.valid || 
                    validationResults.valid === 0 || 
                    (validationResults.productLimitInfo && validationResults.productLimitInfo.wouldExceed)
                  }
                  className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center font-semibold"
                >
                  {validationResults.valid ? t('bulkUpload.step3.importProducts') : t('bulkUpload.step3.cannotImport')}
                  {validationResults.valid > 0 && <ArrowRight className="w-5 h-5 ml-2" />}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Importing */}
          {currentStep === 4 && importing && (
            <div className="text-center py-12">
              <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('bulkUpload.step4.importing')}</h2>
              <p className="text-gray-600">{t('bulkUpload.step4.pleaseWait')}</p>
            </div>
          )}

          {/* Step 5: Results */}
          {currentStep === 5 && importResults && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('bulkUpload.step5.title')}</h2>
              
              {/* Success Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {t('bulkUpload.step5.productsImported', { count: importResults.success })}
                </div>
                <p className="text-green-800">{t('bulkUpload.step5.successMessage')}</p>
              </div>

              {/* Failed Imports */}
              {importResults.failed > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <XCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-900 mb-2">
                        {t('bulkUpload.step5.failedImports', { count: importResults.failed })}
                      </h4>
                      <div className="max-h-48 overflow-y-auto">
                        {importResults.errors.map((err, idx) => (
                          <div key={idx} className="text-red-800 text-sm mb-1">
                            <strong>{t('bulkUpload.step3.row')} {err.row}:</strong> {err.product} - {err.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={resetWizard}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50"
                >
                  {t('bulkUpload.step5.uploadMore')}
                </button>
                <button
                  onClick={() => navigate('/wholesaler/products')}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-semibold"
                >
                  {t('bulkUpload.step5.viewProducts')}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* ============================================ */}
        {/* BULK IMAGE UPLOAD SECTION */}
        {/* ============================================ */}
        <BulkImageUploadSection t={t} />
      </main>
      <Footer />
    </div>
  );
};

// ============================================
// BULK IMAGE UPLOAD COMPONENT
// ============================================
const BulkImageUploadSection = ({ t }) => {
  const [imageFiles, setImageFiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  // Load products for SKU matching preview
  useEffect(() => {
    wholesalerAPI.getProducts().then(res => {
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setProducts(data);
      setProductsLoaded(true);
    }).catch(() => setProductsLoaded(true));
  }, []);

  const skuSet = new Set(products.filter(p => p.sku).map(p => p.sku));

  const extractSku = (filename) => {
    const nameWithoutExt = filename.replace(/\.[^.]+$/, '');
    return nameWithoutExt.replace(/_\d+$/, '');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 200) {
      toast.error(t('bulkImages.maxFiles'));
      return;
    }
    setImageFiles(files);
    setUploadResults(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f =>
      ['image/jpeg', 'image/png', 'image/webp'].includes(f.type)
    );
    if (files.length > 200) {
      toast.error(t('bulkImages.maxFiles'));
      return;
    }
    setImageFiles(files);
    setUploadResults(null);
  }, [t]);

  const handleUpload = async () => {
    if (imageFiles.length === 0) return;
    setUploading(true);
    setUploadResults(null);

    const formData = new FormData();
    imageFiles.forEach(file => formData.append('images', file));

    try {
      const res = await wholesalerAPI.bulkUploadImages(formData);
      setUploadResults(res.data.data || res.data);
      setImageFiles([]);
    } catch (err) {
      const msg = err.response?.data?.message || 'Upload failed';
      setUploadResults({ error: msg });
    } finally {
      setUploading(false);
    }
  };

  // Build preview data
  const previewData = imageFiles.map(file => {
    const sku = extractSku(file.originalname || file.name);
    return { name: file.name, sku, found: skuSet.has(sku) };
  });

  const matchedCount = previewData.filter(p => p.found).length;
  const unmatchedCount = previewData.filter(p => !p.found).length;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mt-8">
      <div className="flex items-center mb-4">
        <Image className="w-8 h-8 text-purple-600 mr-3" />
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('bulkImages.title')}</h2>
          <p className="text-gray-600">{t('bulkImages.description')}</p>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <p className="text-purple-800 text-sm font-medium">{t('bulkImages.namingConvention')}</p>
        <p className="text-purple-700 text-xs mt-1">{t('bulkImages.maxFiles')} &bull; {t('bulkImages.maxPerProduct')}</p>
      </div>

      {/* Drop zone */}
      {!uploading && !uploadResults && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 text-center hover:border-purple-500 transition-colors cursor-pointer"
          onClick={() => document.getElementById('bulk-image-upload').click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">{t('bulkImages.dropzone')}</p>
          <p className="text-gray-400 text-sm mt-1">.jpg, .jpeg, .png, .webp</p>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="bulk-image-upload"
          />
        </div>
      )}

      {/* Files selected - Preview table */}
      {imageFiles.length > 0 && !uploading && !uploadResults && (
        <>
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {t('bulkImages.filesSelected', { count: imageFiles.length })}
            </span>
            {productsLoaded && (
              <div className="flex gap-3 text-sm">
                <span className="text-green-600">{matchedCount} {t('bulkImages.found')}</span>
                <span className="text-red-500">{unmatchedCount} {t('bulkImages.notFound')}</span>
              </div>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto border rounded-lg mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">{t('bulkImages.fileName')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">{t('bulkImages.extractedSku')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">{t('bulkImages.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {previewData.slice(0, 50).map((item, idx) => (
                  <tr key={idx} className={item.found ? '' : 'bg-red-50'}>
                    <td className="px-4 py-2 text-sm text-gray-800 truncate max-w-[200px]">{item.name}</td>
                    <td className="px-4 py-2 text-sm font-mono text-gray-700">{item.sku}</td>
                    <td className="px-4 py-2">
                      {item.found ? (
                        <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                          <CheckCircle className="w-3 h-3 mr-1" />{t('bulkImages.found')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                          <XCircle className="w-3 h-3 mr-1" />{t('bulkImages.notFound')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData.length > 50 && (
              <div className="bg-gray-50 p-2 text-center text-sm text-gray-500">
                ...and {previewData.length - 50} more files
              </div>
            )}
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => { setImageFiles([]); setUploadResults(null); }}
              className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleUpload}
              disabled={imageFiles.length === 0}
              className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 font-semibold flex items-center justify-center"
            >
              <Upload className="w-5 h-5 mr-2" />
              {t('bulkImages.upload')}
            </button>
          </div>
        </>
      )}

      {/* Uploading state */}
      {uploading && (
        <div className="text-center py-12">
          <Loader className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">{t('bulkImages.uploading')}</p>
        </div>
      )}

      {/* Results */}
      {uploadResults && !uploadResults.error && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('bulkImages.results')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{uploadResults.uploaded}</div>
              <div className="text-sm text-gray-600">{t('bulkImages.uploaded')}</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{uploadResults.matched}</div>
              <div className="text-sm text-gray-600">{t('bulkImages.matched')}</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{uploadResults.unmatched}</div>
              <div className="text-sm text-gray-600">{t('bulkImages.unmatched')}</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">{uploadResults.skipped}</div>
              <div className="text-sm text-gray-600">{t('bulkImages.skipped')}</div>
            </div>
          </div>

          {/* Unmatched files list */}
          {uploadResults.unmatchedFiles && uploadResults.unmatchedFiles.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-red-900 mb-2">{t('bulkImages.unmatchedFiles')}</h4>
              <div className="max-h-32 overflow-y-auto text-sm text-red-800">
                {uploadResults.unmatchedFiles.map((f, i) => <div key={i}>{f}</div>)}
              </div>
            </div>
          )}

          {/* Errors */}
          {uploadResults.errors && uploadResults.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-red-900 mb-2">{t('bulkImages.errors')}</h4>
              <div className="max-h-32 overflow-y-auto text-sm text-red-800">
                {uploadResults.errors.map((e, i) => <div key={i}>{e.file}: {e.error}</div>)}
              </div>
            </div>
          )}

          <button
            onClick={() => { setUploadResults(null); setImageFiles([]); }}
            className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 font-semibold"
          >
            {t('bulkImages.uploadAnother')}
          </button>
        </div>
      )}

      {/* Error state */}
      {uploadResults && uploadResults.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <XCircle className="w-6 h-6 text-red-600 mr-2" />
            <span className="text-red-800">{uploadResults.error}</span>
          </div>
          <button
            onClick={() => { setUploadResults(null); }}
            className="mt-4 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  );
};

export default BulkProductUpload;